/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  EMPTY_FORM_STATE_SNAPSHOT,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getInvalidFields,
  isEntityInvalid,
  setEntityFields,
} from '@gh/common-shared';
import { EMPTY_ID, ID, WithDirty, WithFormMode, WithVersion } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { cast, EMPTY_ARRAY, isNotNil } from '@gh/core-util';
import { compact, first, flatMap, flow, isEmpty, isNil, last, omit, reverse, uniqBy } from 'lodash';
import {
  getJobCsDetailsCcId,
  getJobCsDetailsHeadingId,
  getJobCsDetailsItemId,
  getJobCsDetailsSubheadingId,
  JobCsDetails,
  JobCsDetailsCc,
  JobCsDetailsHeading,
  JobCsDetailsItem,
  JobCsDetailsLevel,
  JobCsDetailsMasterInfo,
  JobCsDetailsSubheading,
  jobCsHeadingArr,
  jobCsItemArr,
  jobCsSubheadingArr,
  validateJobCsDetailsMasterInfo,
} from '../../resources/job-cs-details.model';

export enum JobCsDetailsFocusedTable {
  Headings = 'Heading',
  Subheadings = 'Subheading',
  Items = 'Items',
  CostCenters = 'CostCenters',
  QuoteChanges = 'QuoteChanges',
}

export enum JobCsDetailsStateEventType {
  Load = 'Load',
  SetMasterInfo = 'SetMasterInfo',
  Merge = 'Merge',
  SelectHeading = 'SelectHeading',
  SelectSubheading = 'SelectSubheading',
  SelectItem = 'SelectItem',
  ResetFocusedCell = 'ResetFocusedCell',
  NavigateToInvalid = 'NavigateToInvalid',
  SettingSetField = 'SettingSetField',
  HeadingAdd = 'HeadingAdd',
  HeadingMove = 'HeadingMove',
  HeadingRemove = 'HeadingRemove',
  HeadingSetField = 'HeadingSetField',
  SubheadingAdd = 'SubheadingAdd',
  SubheadingMove = 'SubheadingMove',
  SubheadingRemove = 'SubheadingRemove',
  SubheadingSetField = 'SubheadingSetField',
  ItemAdd = 'ItemAdd',
  ItemMove = 'ItemMove',
  ItemRemove = 'ItemRemove',
  ItemSetField = 'ItemSetField',
  CostCenterAdd = 'CostCenterAdd',
  CostCenterRemove = 'CostCenterRemove',
  CostCenterSetField = 'CostCenterSetField',
  QuoteChangeSetField = 'QuoteChangeSetField',
  MoveToItems = 'MoveToItems',
  BackToQuoteChanges = 'BackToQuoteChanges',
  Undo = 'Undo',
  Redo = 'Redo', 
  AddToHistory = 'AddToHistory',
  InitHistory = 'InitHistory',
  MarkContactMenuChange = 'MarkContactMenuChange',
}

export type JobCsDetailsStateLoadEvent = {
  type: JobCsDetailsStateEventType.Load;
  details: JobCsDetails;
  mode?: FormMode;
  markAsDirty?: boolean;
};
export type JobCsDetailsStateMergeEvent = { type: JobCsDetailsStateEventType.Merge; details: JobCsDetails };
export type JobCsDetailsStateSetMasterInfoEvent = {
  type: JobCsDetailsStateEventType.SetMasterInfo;
  masterInfo: FormStateSnapshot<JobCsDetailsMasterInfo>;
};

export type JobCsDetailsStateSelectHeadingEvent = { type: JobCsDetailsStateEventType.SelectHeading; headingId: ID };
export type JobCsDetailsStateSelectSubheadingEvent = {
  type: JobCsDetailsStateEventType.SelectSubheading;
  headingId: ID;
  subheadingId: ID;
};
export type JobCsDetailsStateSelectItemEvent = {
  type: JobCsDetailsStateEventType.SelectItem;
  headingId: ID;
  subheadingId: ID;
  itemId: ID;
};
export type JobCsDetailsStateResetFocusedCellEvent = { type: JobCsDetailsStateEventType.ResetFocusedCell };
export type JobCsDetailsStateNavigateToInvalidEvent = { type: JobCsDetailsStateEventType.NavigateToInvalid };

export type JobCsDetailsStateSettingSetFieldEvent = {
  type: JobCsDetailsStateEventType.SettingSetField;
  name: keyof JobCsDetailsSettings;
  value: any;
};

export type JobCsDetailsStateHeadingAddEvent = {
  type: JobCsDetailsStateEventType.HeadingAdd;
  entity: JobCsDetailsHeading;
  afterId?: ID;
};
export type JobCsDetailsStateHeadingRemoveEvent = {
  type: JobCsDetailsStateEventType.HeadingRemove;
  id: ID;
};
export type JobCsDetailsStateHeadingMoveEvent = {
  type: JobCsDetailsStateEventType.HeadingMove;
  fromId: ID;
  toId: ID;
};
export type JobCsDetailsStateHeadingSetFieldEvent = {
  type: JobCsDetailsStateEventType.HeadingSetField;
  id: ID;
  name: string;
  value: any;
};

export type JobCsDetailsStateSubheadingAddEvent = {
  type: JobCsDetailsStateEventType.SubheadingAdd;
  headingId: ID;
  entity: JobCsDetailsSubheading;
  afterId?: ID;
};
export type JobCsDetailsStateSubheadingRemoveEvent = {
  type: JobCsDetailsStateEventType.SubheadingRemove;
  headingId: ID;
  id: ID;
};
export type JobCsDetailsStateSubheadingMoveEvent = {
  type: JobCsDetailsStateEventType.SubheadingMove;
  headingId: ID;
  fromId: ID;
  toId: ID;
};
export type JobCsDetailsStateSubheadingSetFieldEvent = {
  type: JobCsDetailsStateEventType.SubheadingSetField;
  headingId: ID;
  id: ID;
  name: string;
  value: any;
};

export type JobCsDetailsStateItemAddEvent = {
  type: JobCsDetailsStateEventType.ItemAdd;
  headingId: ID;
  subheadingId: ID;
  entity: JobCsDetailsItem;
};
export type JobCsDetailsStateItemRemoveEvent = {
  type: JobCsDetailsStateEventType.ItemRemove;
  headingId: ID;
  subheadingId: ID;
  id: ID;
};
export type JobCsDetailsStateItemMoveEvent = {
  type: JobCsDetailsStateEventType.ItemMove;
  headingId: ID;
  subheadingId: ID;
  fromId: ID;
  toId: ID;
};
export type JobCsDetailsStateItemSetFieldEvent = {
  type: JobCsDetailsStateEventType.ItemSetField;
  headingId: ID;
  subheadingId: ID;
  id: ID;
  name: string;
  value: any;
};
export type JobCsDetailsStateMoveToItemsEvent = {
  type: JobCsDetailsStateEventType.MoveToItems;
  headingId: ID;
  subheadingId: ID;
  quoteChange: JobCsDetailsItem;
};
export type JobCsDetailsStateBackToQuoteChangesEvent = {
  type: JobCsDetailsStateEventType.BackToQuoteChanges;
  headingId: ID;
  subheadingId: ID;
  item: JobCsDetailsItem;
};

export type JobCsDetailsStateCostCenterAddEvent = {
  type: JobCsDetailsStateEventType.CostCenterAdd;
  headingId: ID;
  subheadingId: ID;
  itemId: ID;
  entities: JobCsDetailsCc[];
};
export type JobCsDetailsStateCostCenterRemoveEvent = {
  type: JobCsDetailsStateEventType.CostCenterRemove;
  headingId: ID;
  subheadingId: ID;
  itemId: ID;
  id: ID;
};
export type JobCsDetailsStateCostCenterSetFieldEvent = {
  type: JobCsDetailsStateEventType.CostCenterSetField;
  headingId: ID;
  subheadingId: ID;
  itemId: ID;
  id: ID;
  name: string;
  value: any;
};
export type JobCsDetailsStateQuoteChangeSetFieldEvent = {
  type: JobCsDetailsStateEventType.QuoteChangeSetField;
  headingId: ID;
  subheadingId: ID;
  id: ID;
  name: string;
  value: any;
};

export type JobCsDetailsStateInitHistoryEvent = { type: JobCsDetailsStateEventType.InitHistory };
export type JobCsDetailsStateAddToHistoryEvent = { type: JobCsDetailsStateEventType.AddToHistory };
export type JobCsDetailsStateUndoEvent = { type: JobCsDetailsStateEventType.Undo };
export type JobCsDetailsStateRedoEvent = { type: JobCsDetailsStateEventType.Redo };
export type JobCsDetailsStateMarkContactMenuChangeEvent = { type: JobCsDetailsStateEventType.MarkContactMenuChange };

export type JobCsDetailsStateEvent = JobCsDetailsStateLoadEvent
  | JobCsDetailsStateMergeEvent
  | JobCsDetailsStateSetMasterInfoEvent
  | JobCsDetailsStateSelectHeadingEvent | JobCsDetailsStateSelectSubheadingEvent | JobCsDetailsStateSelectItemEvent
  | JobCsDetailsStateNavigateToInvalidEvent | JobCsDetailsStateResetFocusedCellEvent
  | JobCsDetailsStateSettingSetFieldEvent
  | JobCsDetailsStateHeadingAddEvent | JobCsDetailsStateHeadingRemoveEvent | JobCsDetailsStateHeadingSetFieldEvent
  | JobCsDetailsStateHeadingMoveEvent
  | JobCsDetailsStateSubheadingAddEvent | JobCsDetailsStateSubheadingRemoveEvent
  | JobCsDetailsStateSubheadingSetFieldEvent | JobCsDetailsStateSubheadingMoveEvent
  | JobCsDetailsStateItemAddEvent | JobCsDetailsStateItemRemoveEvent | JobCsDetailsStateItemSetFieldEvent
  | JobCsDetailsStateItemMoveEvent
  | JobCsDetailsStateCostCenterAddEvent | JobCsDetailsStateCostCenterRemoveEvent
  | JobCsDetailsStateCostCenterSetFieldEvent
  | JobCsDetailsStateMoveToItemsEvent
  | JobCsDetailsStateBackToQuoteChangesEvent
  | JobCsDetailsStateQuoteChangeSetFieldEvent
  | JobCsDetailsStateInitHistoryEvent
  | JobCsDetailsStateAddToHistoryEvent
  | JobCsDetailsStateUndoEvent
  | JobCsDetailsStateRedoEvent
  | JobCsDetailsStateMarkContactMenuChangeEvent;

export type JobCsItemPath = [JobCsDetailsHeading, JobCsDetailsSubheading, JobCsDetailsItem];

export type JobCsDetailsSelection = {
  headingId?: ID;
  subheadingId?: ID;
  itemId?: ID;
  focusedTable?: JobCsDetailsFocusedTable;
  focusedCell?: FocusedCell;
};

export type JobCsDetailsSettings = {
  ccClone: boolean;
  ccRequired: boolean;
};

export type JobCsDetailsState = WithDirty<WithFormMode<WithVersion<{
  jobId: ID;
  csMasterId: ID;
  masterInfo: FormStateSnapshot<JobCsDetailsMasterInfo>;
  headings: JobCsDetailsHeading[];
  quoteChanges?: JobCsDetailsHeading;
  selection: JobCsDetailsSelection;
  settings: JobCsDetailsSettings;
  hadEmptyItems: boolean;
  history?: JobCsDetailsState[];
  historyPosition: number;
  historyLength: number;
  hasOnlyContactMenuChanges: boolean;
}>>>;

export const JOB_CS_DETAILS_INIT_STATE: JobCsDetailsState = {
  formMode: FormMode.EditMode,
  jobId: EMPTY_ID,
  csMasterId: EMPTY_ID,
  masterInfo: EMPTY_FORM_STATE_SNAPSHOT,
  headings: [],
  isDirty: false,
  selection: {},
  settings: { ccClone: true, ccRequired: true },
  hadEmptyItems: false,
  history: [],
  historyPosition: 0,
  historyLength: 12,
  hasOnlyContactMenuChanges: false,
};

export const queryJobCsId = (state: JobCsDetailsState) => state.jobId;
export const queryJobCsMasterInfoHasErrors = (state: JobCsDetailsState) =>
  flow(queryJobCsMasterInfo, getFormStateSnapshotValue, validateJobCsDetailsMasterInfo, isNotNil)(state);
/**
 * In order to define whether whole structure has errors we should traverse only top level headings,
 * because entity validation state is propagated to parent entities
 */
export const queryJobCsStructureHasErrors = (state: JobCsDetailsState) =>
  queryJobCsHeadings(state).filter(isEntityInvalid).length > 0;
export const queryJobCsHasErrors = (state: JobCsDetailsState) =>
  queryJobCsMasterInfoHasErrors(state) || queryJobCsStructureHasErrors(state);
export const queryJobCsSaveModel = (state: JobCsDetailsState) => <JobCsDetails>({
// @ts-expect-error Legacy typing suppression
  jobId: queryJobCsId(state),
// @ts-expect-error Legacy typing suppression
  csMasterId: state.csMasterId,
  ...cast<JobCsDetails>(omit(getFormStateSnapshotValue(state.masterInfo), 'csSignedByClient', 'csSignedByOffice')),
  headings: state.headings,
  quoteChanges: state.quoteChanges,
});

export const queryJobCsFocusedCell = ({ selection }: JobCsDetailsState, table: JobCsDetailsFocusedTable) =>
  selection.focusedTable === table ? selection.focusedCell : void 0;

export const queryJobCsMasterInfo = (state: JobCsDetailsState) => state.masterInfo;
export const queryJobCsHeadings = (state: JobCsDetailsState) => state.headings;
export const queryJobCsSelectedHeadingId = ({ selection: { headingId } }: JobCsDetailsState) => headingId;
export const queryJobCsSelectedSubheadingId = ({ selection: { subheadingId } }: JobCsDetailsState) => subheadingId;
export const queryJobCsSelectedItemId = ({ selection: { itemId } }: JobCsDetailsState) => itemId;
export const queryJobCsFocusedHeadingCell = (state: JobCsDetailsState) =>
  queryJobCsFocusedCell(state, JobCsDetailsFocusedTable.Headings);
export const queryJobCsFocusedSubheadingCell = (state: JobCsDetailsState) =>
  queryJobCsFocusedCell(state, JobCsDetailsFocusedTable.Subheadings);
export const queryJobCsFocusedItemCell = (state: JobCsDetailsState) =>
  queryJobCsFocusedCell(state, JobCsDetailsFocusedTable.Items);
export const queryJobCsFocusedCcCell = (state: JobCsDetailsState) =>
  queryJobCsFocusedCell(state, JobCsDetailsFocusedTable.CostCenters);
export const queryJobCsFocusedQcCell = (state: JobCsDetailsState) =>
  queryJobCsFocusedCell(state, JobCsDetailsFocusedTable.QuoteChanges);

export const queryJobCsHeading = ({ headings }: JobCsDetailsState, headingId: ID) =>
  headingId ? jobCsHeadingArr.find(headings, headingId) : void 0;
export const queryJobCsSelectedHeading = (state: JobCsDetailsState) => {
  const { headingId } = state.selection;
  return headingId ? queryJobCsHeading(state, headingId) : void 0;
};
export const queryJobCsCurrentSubheadings = (state: JobCsDetailsState) => {
  const heading = queryJobCsSelectedHeading(state);
  return heading ? heading.subHeadings : EMPTY_ARRAY;
};
export const queryJobCsCurrentSubheading = (state: JobCsDetailsState, subheadingId: ID) => {
  const subheadings = queryJobCsCurrentSubheadings(state);
  return subheadingId ? jobCsSubheadingArr.find(subheadings, subheadingId) : void 0;
};
export const queryJobCsSelectedSubheading = (state: JobCsDetailsState) => {
  const { subheadingId } = state.selection;
  return subheadingId ? queryJobCsCurrentSubheading(state, subheadingId) : void 0;
};
export const queryJobCsCurrentItems = (state: JobCsDetailsState): JobCsDetailsItem[] => {
  const subheading = queryJobCsSelectedSubheading(state);
  return subheading ? subheading.items : EMPTY_ARRAY;
};
export const queryJobCsCurrentItem = (state: JobCsDetailsState, itemId: ID) => {
  const items = queryJobCsCurrentItems(state);
  return itemId ? jobCsItemArr.find(items, itemId) : void 0;
};
export const queryJobCsSelectedItem = (state: JobCsDetailsState) => {
  const { itemId } = state.selection;
  return itemId ? queryJobCsCurrentItem(state, itemId) : void 0;
};
export const queryJobCsCurrentQuoteChanges = ({ quoteChanges }: JobCsDetailsState): JobCsDetailsItem[] => {
  const qcSubheading = quoteChanges ? first(quoteChanges.subHeadings) : void 0;
  return qcSubheading ? qcSubheading.items : EMPTY_ARRAY;
};
export const queryJobCsCurrentQuoteChange = (state: JobCsDetailsState, itemId: ID) => {
  const items = queryJobCsCurrentQuoteChanges(state);
  return itemId ? jobCsItemArr.find(items, itemId) : void 0;
};
export const queryJobCsCurrentCostCenters = (state: JobCsDetailsState): JobCsDetailsCc[] => {
  const item = queryJobCsSelectedItem(state);
  return item ? item.costCenters : EMPTY_ARRAY;
};

export const queryJobCsQuoteChanges = ({ quoteChanges }: JobCsDetailsState) => {
  const qcSubheading = quoteChanges ? first(quoteChanges.subHeadings) : void 0;
  return qcSubheading ? qcSubheading.items : EMPTY_ARRAY;
};

export const queryJobCsSettings = ({ settings }: JobCsDetailsState) => settings;

export const queryJobCsCurrentSubheadingCostCenters = (state: JobCsDetailsState) =>
  uniqBy(
    flatMap(
      queryJobCsCurrentItems(state),
      (item) => item.costCenters.filter((cc) => cc.costCenter)),
    (cc) => cc.costCenterId);

export const queryJobCsEmptyItems = (state: JobCsDetailsState): JobCsItemPath[] =>
  reverse(flatMap(state.headings, (heading) =>
    flatMap(heading.subHeadings, (subheading) =>
      <JobCsItemPath[]>subheading.items
        .filter((item) => isEmpty(item.costCenters))
        .map((item) => [heading, subheading, item]))));

export const queryJobCsInvalidPath = (state: JobCsDetailsState): Maybe<any[]> => {
  const invalidHeading = first(queryJobCsHeadings(state).filter(isEntityInvalid));
  const invalidSubheading = invalidHeading ? first(invalidHeading.subHeadings.filter(isEntityInvalid)) : void 0;
  const invalidItem = invalidSubheading ? first(invalidSubheading.items.filter(isEntityInvalid)) : void 0;
  const invalidCc = invalidItem ? first(invalidItem.costCenters.filter(isEntityInvalid)) : void 0;

  const invalidPath = compact([invalidHeading, invalidSubheading, invalidItem, invalidCc]);
  return invalidPath.length > 0 ? invalidPath : void 0;
};

export const queryJobCsHadEmptyItems = (state: JobCsDetailsState) => state.hadEmptyItems;
export const queryJobCsDocSigningId = (state: JobCsDetailsState) =>
  getFormStateSnapshotValue(state.masterInfo).csDocSigningId;

export const getInvalidPathElementId = (path: any[], index: number): Maybe<ID> => {
  if (isNil(path[index])) {
    return;
  }

  switch (index) {
    case JobCsDetailsLevel.Heading:
      return getJobCsDetailsHeadingId(path[index]);
    case JobCsDetailsLevel.Subheading:
      return getJobCsDetailsSubheadingId(path[index]);
    case JobCsDetailsLevel.Item:
      return getJobCsDetailsItemId(path[index]);
    case JobCsDetailsLevel.CostCenter:
      return getJobCsDetailsCcId(path[index]);
    default:
      throw new Error(`Level of Job Color Selection is out of bounds: ${index}`);
  }
};

const FOCUSED_TABLE_INDEX = [
  JobCsDetailsFocusedTable.Headings,
  JobCsDetailsFocusedTable.Subheadings,
  JobCsDetailsFocusedTable.Items,
  JobCsDetailsFocusedTable.CostCenters,
  JobCsDetailsFocusedTable.QuoteChanges,
];
export const getInvalidPathFocusedTable = (path: any[]): JobCsDetailsFocusedTable =>
  FOCUSED_TABLE_INDEX[path.length - 1];

export const getInvalidPathFocusedCell = (path: any[]): Maybe<FocusedCell> => {
  const lastInvalid = last(path);
  const invalidField = lastInvalid ? first(getInvalidFields(lastInvalid)) : void 0;
  return lastInvalid && invalidField ? {
    id: getInvalidPathElementId(path, path.length - 1),
    field: invalidField,
  } : void 0;
};

export const buildItemPathName = (path: JobCsItemPath) => path.map((item) => item.name).join(' / ');

export function initJobCsHistory(state: JobCsDetailsState): JobCsDetailsState {
  const newHistory = {
    ...omit(state, 'history')
  } as JobCsDetailsState;
  return setEntityFields(state, {
    historyPosition: 0,
    history: [newHistory],
    hasOnlyContactMenuChanges: false,
  });
}

export function addJobCsToHistory(state: JobCsDetailsState): JobCsDetailsState {
  const history = state.history ? state.history.slice(0, state.historyPosition + 1) : [];  
  const newHistory = {
    ...omit(state, 'history')
  } as JobCsDetailsState;  
  if (history.length <= state.historyLength) {
    history.push(newHistory);
  } else {
    history.shift();
    history.push(newHistory);
  }
  const historyPosition = history.length - 1;
  return setEntityFields(state, {
    historyPosition,
    history,
  });
}

export const queryJobCsUndoDisabled = (state: JobCsDetailsState) => {
  if (!state.history || !state.history.length || state.history.length === 1 || state.historyPosition === 0) {
    return true;
  }
  return false;
};

export const queryJobCsRedoDisabled = (state: JobCsDetailsState) => !state.history || !state.history.length ||
  state.historyPosition === state.history.length - 1;

export function undoJobCs(state: JobCsDetailsState): JobCsDetailsState {
  if (queryJobCsUndoDisabled(state)) {
    return state;
  }
  const history = state.history ? [...state.history] : [];
  const historyPosition = state.historyPosition - 1;
  const undoState = history[historyPosition];
  return setEntityFields({...undoState}, {
    historyPosition,
    history,
    isDirty: undoState.isDirty,
  });
}

export function redoJobCs(state: JobCsDetailsState): JobCsDetailsState {
  if (queryJobCsRedoDisabled(state)) {
    return state;
  }
  const history = state.history ? [...state.history] : [];
  const historyPosition = state.historyPosition + 1;
  const redoState = history[historyPosition];
  return setEntityFields({...redoState}, {
    historyPosition,
    history,
    isDirty: redoState.isDirty,
  });
}
